import Page from '../../../../components/PageWrapper';
import { useMemo, useState } from 'react';
import useModal from '../../../../hooks/modalHook';
import useUserAccount from '../../../../hooks/userAccountHook';
import GTable from '../../../../components/elements/GTable';
import { commentOnInsurerReview, fetchAllInsurerReviews, fetchSingleInsurerReview } from '../../../../api/requests';
import Loader from '../../../../components/Loader';
import moment from 'moment';
import Avatar from '../../../../components/Avatar';
import { getNameInitials } from '../../../../utils/miscFunctions';
import TextArea from '../../../../components/form-control/TextArea';
import Button from '../../../../components/form-control/Button';
import StarRating from '../../../../components/StartRating';
import clsx from 'clsx';
import NotFoundComponent from '../../../../components/NotFoundComponent';
import { useQuery } from '@tanstack/react-query';

const index = () => {
  const [loading, setLoading] = useState<boolean>(true);
  const [reviews, setReviews] = useState<any>();
  const [review, setReview] = useState<any>();
  const [reviewID, setReviewID] = useState<number>();
  const [reviewComment, setReviewComment] = useState<any>('');
  const [showPerPage, setShowPerPage] = useState<number>(100);
  const { ModalComponent, updateModalStates, closeModal } = useModal();
  const { user, getInsurer } = useUserAccount();
  const insurerProfile = getInsurer();

  const getInsurerReviews = async () => {
    try {
      const response = await fetchAllInsurerReviews()
      setReviews(response);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const getSingleInsurerReview = async () => {
    if (!reviewID) return;

    try {
      const response = await fetchSingleInsurerReview(reviewID)
      setReview(response);
      
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const payload = {
    comment: reviewComment
  };

  const addComment = () => {
    if (reviewID) {
      commentOnInsurerReview(reviewID, payload)
        .then((res: any) => {
          setReview(res);
        })
        .catch((err: any) => { });
    }
  };

  const columns = useMemo(
    () => [
      {
        Header: 'M',
        columns: [
          {
            Header: '',
            accessor: 'customer.firstName',
            Cell: (row: any) => {
              return (
                <div className="flex items-center gap-3">
                  <Avatar
                    size="xs"
                    src={row.row.original.customer.account.profileImage}
                    nameInitials={getNameInitials(
                      `${row.row.original.customer.firstName} ${row.row.original.customer.lastName}`
                    )}
                  />

                  <span className={clsx('', !row.row.original.comment && 'font-semibold')}>
                    {row.cell.value} {row.row.original.customer.lastName}
                  </span>
                </div>
              );
            }
          },
          {
            Header: '',
            accessor: 'feedback',
            Cell: (row: any) => {
              return (
                <div className="w-fit max-w-md text-ellipsis truncate block">
                  <span className={clsx('', !row.row.original.comment && 'font-semibold')}>
                    {row.cell.value ? row.cell.value : '-'}
                  </span>
                </div>
              );
            }
          },
          {
            Header: '',
            accessor: 'rating',
            Cell: (row: any) => {
              return (
                <div className="">
                  <StarRating value={row.cell.value} max={5} />
                </div>
              );
            }
          },

          {
            Header: '',
            accessor: 'createdOn',
            Cell: (row: any) => {
              return (
                <div className={clsx('', !row.row.original.comment && 'font-semibold')}>
                  {row.cell.value ? moment(row.cell.value).format('MMM Do YY, h:mm a') : '-'}
                </div>
              );
            }
          }
        ]
      }
    ],
    []
  );

  useQuery({
    queryKey: ['getInsurerReviews'],
    queryFn: () => getInsurerReviews()
  });

  useQuery({
    queryKey: ['getSingleInsurerReview', reviewID],
    queryFn: () => getSingleInsurerReview(),
    enabled: !!reviewID
  });

  return (
    <Page title="Reviews">
      <div className="bg-white w-full p-5 h-full">
        <div className="">
          <h2 className="font-semibold text-3xl">Reviews</h2>
          <span className="text-sm text-slate-400">
            View a list of all insurer reviews and associated comments{' '}
          </span>
        </div>
        {loading && (
          <div className="h-screen flex items-center justify-center">
            <Loader message="Fetching reviews. Please wait..." brollyLoader />
          </div>
        )}

        {!loading && reviews && reviews?.length > 0 && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2 ">
              <div className="border border-primary-border rounded overflow-x-auto col-span-2">
                <GTable
                  columns={columns}
                  data={reviews}
                  showSearch={false}
                  tableType={'reviews'}
                  columnClasses=""
                  showHeaders={false}
                  // paginationData={insurersPaginationData}
                  // onPageChange={handlePageChange}
                  showPerPage={showPerPage}
                  onShowPerPageChange={(page: number) => setShowPerPage(page)}
                  onRowClick={(review: any) => {
                    setReviewID(review.id);
                  }}
                />
              </div>
              <div className="border border-primary-border rounded p-5">
                {review ? (
                  <>
                    <div className="flex items-center gap-5 ">
                      <Avatar
                        size="sm"
                        src={review.customer.account.profileImage}
                        nameInitials={getNameInitials(
                          `${review?.customer.firstName} ${review?.customer.lastName}`
                        )}
                      />

                      <div className="">
                        <h1 className="font-semibold">{`${review?.customer.firstName} ${review?.customer.lastName}`}</h1>
                        <span className="text-xs">
                          {moment(review?.createdOn).format('MMM Do YY, h:mm a')}
                        </span>
                      </div>
                    </div>

                    <div className="py-5">
                      <StarRating value={review.rating} max={5} />

                      <div className="py-5">
                        <span className="">{review?.feedback}</span>
                      </div>

                      {review.comment && (
                        <div>
                          <hr className="w-2" />
                          <div className="flex items-center  gap-5 py-5">
                            <Avatar
                              size="sm"
                              src={user.profileImage}
                              nameInitials={getNameInitials(user.fullName)}
                              avatarColour={insurerProfile?.themeColour}
                              initialsColour={`${insurerProfile?.fontColour}`}
                            />

                            <div className="">
                              <span className="font-bold">{user.fullName}</span>
                              <br />
                              <span className="text-xs">
                                {moment(review?.comment?.createdOn).format('MMM Do YY, h:mm a')}
                              </span>
                            </div>
                          </div>
                          <div className="">
                            <span>{review.comment.comment}</span>
                          </div>
                        </div>
                      )}

                      {!review.comment &&
                        (user.isInsurer()) && (
                          <>
                            <TextArea
                              name={'reviewComment'}
                              value={reviewComment}
                              onChange={(e: any) => {
                                setReviewComment(e.target.value);
                              }}
                            />

                            <div className="flex justify-end mt-5">
                              <Button
                                onClick={() => addComment()}
                                label="Comment"
                                curved
                                bgColor="primary"
                              />
                            </div>
                          </>
                        )}
                    </div>
                  </>
                ) : (
                  <div className="">
                    <h1 className="font-bold pt-10 text-lg flex items-center justify-center">
                      No review selected
                    </h1>
                  </div>
                )}
              </div>
            </div>
          </>
        )}

        {!loading && reviews?.length <= 0 && <NotFoundComponent text={'No review found'} />}
      </div>
      <ModalComponent />
    </Page>
  );
};

export default index;
